import { useMemo } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { recipeNutrition, formatMacros } from "../lib/nutrition";

// Per-serving + total macros rolled up across the recipe's
// ingredients. Renders as a compact tile between the recipe meta
// and ingredient list (or wherever the surface mounts it).
//
// Hidden when no ingredient resolves so we don't surface a fake
// "0 kcal" on a recipe the nutrition data doesn't cover. Coverage
// ratio ("based on 7 of 9") is always shown when partial so the
// user knows we're estimating.
//
// Pass the SCALED recipe — recipeNutrition reads `recipe.serves`
// and per-ingredient amounts directly. If the surface scales the
// recipe via scaleRecipe(), the scaled object's ingredients are
// already multiplied, so total calories reflect the user's chosen
// servings.
export default function RecipeNutritionLine({
  recipe, pantry, ingredientInfo, brandNutrition,
  compact = false,
}) {
  const summary = useMemo(
    () => recipeNutrition(recipe, { pantry, getInfo: ingredientInfo?.getInfo, brandNutrition }),
    [recipe, pantry, ingredientInfo, brandNutrition],
  );
  if (!summary || summary.coverage.resolved === 0) return null;
  const { resolved, total } = summary.coverage;
  const partial = resolved < total;
  const kcalPer = Math.round(summary.perServing.kcal || 0);
  const kcalTotal = Math.round(summary.total.kcal || 0);
  const serves = Math.max(1, Number(recipe?.serves) || 1);
  const formattedTotal = kcalTotal >= 1000
    ? kcalTotal.toLocaleString()
    : String(kcalTotal);

  return (
    <div style={{
      marginTop: compact ? 8 : 10,
      padding: compact ? "8px 10px" : "10px 14px",
      background: COLOR.ground,
      border: `1px solid ${COLOR.edge}`,
      borderRadius: 10,
      textAlign: "center",
    }}>
      <div style={{
        fontFamily: FONT.forma, fontSize: compact ? 12 : 13, fontWeight: 600,
        color: COLOR.ink,
      }}>
        ≈ {kcalPer.toLocaleString()} kcal/serving
        <span style={{ color: COLOR.muted, fontWeight: 500 }}>
          {" "}· {formattedTotal} kcal total
        </span>
      </div>
      {!compact && (
        <div style={{
          marginTop: 4,
          fontFamily: FONT.forma, fontSize: 10, fontWeight: 500,
          color: COLOR.muted,
        }}>
          {formatMacros(summary.perServing, { verbose: true })} per serving · {serves} {serves === 1 ? "serving" : "servings"}
          {partial ? ` · based on ${resolved} of ${total} ingredients` : ""}
        </div>
      )}
      {compact && partial && (
        <div style={{
          marginTop: 2,
          fontFamily: FONT.forma, fontSize: 9, fontWeight: 500,
          color: COLOR.muted, letterSpacing: "0.04em",
        }}>
          based on {resolved} of {total} ingredients
        </div>
      )}
    </div>
  );
}
